/**
 * Workflows library (Tier 1 "Understand", spec 2026-07-04 §6.1).
 * Route: /workflows — gated by workflow_transparency + workflows:read.
 * Read-only: rows link into the per-workflow overview.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Workflow as WorkflowIcon } from 'lucide-react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useWorkflowsLibrary, type WorkflowSummary } from '@/hooks/useWorkflows'
import { useCanAccessFeature } from '@/hooks'
import { Button, EmptyState } from '@/components/ui'
import { WorkflowGalleryCard } from '@/components/workflows/WorkflowGalleryCard'
import { ReadinessChip } from '@/components/workflows/ReadinessChip'
import { intakeRelation } from '@/utils/intakeRelation'
import { ForkWorkflowDialog } from '@/components/workflows/ForkWorkflowDialog'
import { PackInstallDialog } from './WorkflowOverview/packs/PackInstallDialog'
import { DeploymentVocabularyDialog } from './WorkflowOverview/packs/DeploymentVocabularyDialog'

/** E1.5 ownership lanes (spec 2026-09-18 §4.4): only platform rows of the
 * two forkable kinds get "New from type" — the same check the fork
 * endpoint makes, so the card never offers what the server would refuse. */
function isForkableType(wf: WorkflowSummary): boolean {
  return wf.source === 'platform' && wf.kind !== 'graph'
}

export function WorkflowsPage() {
  const { data: workflows, isLoading, isError } = useWorkflowsLibrary()
  const canInstallPacks = useCanAccessFeature('workflow_configuration', 'workflows:configure')
  // Final-review I2: same predicate as the Readiness tab (and the endpoint's
  // own gate — `workflows:read` + the `workflow_configuration` feature). A
  // viewer the readiness endpoint would refuse gets no chip and no fetch.
  const canReadiness = useCanAccessFeature('workflow_configuration', 'workflows:read')
  // E1.5: the fork endpoint rides the workflows router (workflow_engine)
  // under workflows:author.
  const canAuthor = useCanAccessFeature('workflow_engine', 'workflows:author')
  const [packOpen, setPackOpen] = useState(false)
  // E4 (SA-385, spec D22): the deployment-vocabulary pointer rides the same
  // gate as the install wizard (workflow_configuration + workflows:configure
  // — the PUT's own gate).
  const [vocabularyOpen, setVocabularyOpen] = useState(false)
  const [forkTarget, setForkTarget] = useState<WorkflowSummary | null>(null)

  return (
    <PageFrame width="list">
      <PageHeader
        title="Workflows"
        icon={WorkflowIcon}
        sticky
        subtitle="How your AI runs each guided process — open one to see exactly how it works."
        actions={canInstallPacks && (
          <>
            <Button variant="outline" onClick={() => setVocabularyOpen(true)}>
              Deployment vocabulary
            </Button>
            <Button variant="outline" onClick={() => setPackOpen(true)}>
              Starter packs
            </Button>
          </>
        )}
      />
      <PackInstallDialog open={packOpen} onClose={() => setPackOpen(false)} />
      <DeploymentVocabularyDialog
        open={vocabularyOpen}
        onClose={() => setVocabularyOpen(false)}
      />
      {forkTarget && (
        <ForkWorkflowDialog
          // Keyed so a different card starts with a fresh name field.
          key={forkTarget.id}
          workflow={forkTarget}
          mode="new_from_type"
          open
          onOpenChange={(o) => { if (!o) setForkTarget(null) }}
        />
      )}

      {isLoading && <p className="py-8 text-sm text-muted-foreground">Loading workflows…</p>}
      {isError && (
        <p className="py-8 text-sm text-destructive">
          Couldn't load workflows. You may not have permission to view them.
        </p>
      )}

      {!isLoading && !isError && (workflows ?? []).length === 0 && (
        <EmptyState
          icon={WorkflowIcon}
          title="No workflows yet"
          description="Platform templates appear here once they are installed for this deployment."
        />
      )}

      {/* The cards size by the grid's own width, not the viewport, which cannot
          see the sidebar or its collapse (SA-1188). */}
      <div
        data-testid="workflow-gallery"
        className="grid grid-cols-[repeat(auto-fill,minmax(min(20rem,100%),1fr))] gap-4 mt-4"
      >
        {(workflows ?? []).map((wf) => (
          // The card is one Link; the fork action sits beneath it in the
          // same grid cell so no interactive element nests inside the link.
          <div key={wf.id} className="flex h-full flex-col gap-2">
            <Link
              to={`/workflows/${wf.id}`}
              className="block flex-1 rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition"
            >
              <WorkflowGalleryCard
                workflow={wf}
                relation={intakeRelation(wf, workflows ?? [])}
                footer={
                  <>
                    {wf.bound_personas.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Used by:{' '}
                        {wf.bound_personas.map((bp, i) => (
                          <span key={bp.persona_id}>
                            {i > 0 && ', '}
                            <span className="font-medium text-foreground">{bp.persona_name}</span>
                            {' '}({bp.channel})
                          </span>
                        ))}
                      </p>
                    )}
                    {canReadiness && wf.manifest && (
                      <div className="mt-1">
                        <ReadinessChip workflowId={wf.id} />
                      </div>
                    )}
                  </>
                }
              />
            </Link>
            {canAuthor && isForkableType(wf) && (
              <div className="flex justify-end px-1">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`New from type: ${wf.name}`}
                  onClick={() => setForkTarget(wf)}
                >
                  New from type
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </PageFrame>
  )
}
